import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TuiActiveZoneModule, TuiLetModule } from '@taiga-ui/cdk';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  delay,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  skip,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AsyncPipe, NgForOf } from '@angular/common';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import {
  TuiDataListDropdownManagerModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import {
  fromFormControlValue,
  fromFormGroupValue,
} from '@shared/utils/observables';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDialogService,
  TuiDropdownModule,
  TuiGroupModule,
  TuiHintModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import CatalogsState from '@store/catalogs/catalogs.state';
import { Store } from '@ngxs/store';
import { CargoPlaceType } from '@shared/constants/cargo-places';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@shared/services/alert.service';
import { FormsService } from '@shared/services/forms.service';
import { CompareWithFieldDirective } from '@shared/directives/compare-with-field.directive';
import {
  BuyerFormComponent,
  BuyerFormInputData,
} from '@shared/components/catalogs/buyer-form/buyer-form.component';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import {
  ClientFormComponent,
  ClientFormInputData,
} from '@shared/components/catalogs/client-form/client-form.component';
import {
  FarmFormComponent,
  FarmFormInputData,
} from '@shared/components/catalogs/farm-form/farm-form.component';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { CommonStateService } from '../../../../services/common-state.service';
import { CargoLoadingService } from '../../../../services/cargo-loading.service';
import { CargoLoadingState } from '../../../../store/cargo-loading.state';
import {
  CargoOrder,
  CargoOrderForm,
  ClientJson,
  OrderCargoPlaceDetailsForm,
} from '../../../../../../types/cargo';
import { PreOrder } from '../../../../../../types/pre-order';
import { CargoPlaceDetailsComponent } from '../cargo-place-details/cargo-place-details.component';
import { Farm } from '../../../../../../types/farm';
import { CustomLocationDialogComponent } from '../custom-location-dialog/custom-location-dialog.component';
import { AddressWithCoordinates } from '../../../../../../types/client';

@Component({
  selector: 'app-order',
  standalone: true,
  imports: [
    TuiActiveZoneModule,
    AsyncPipe,
    AutocompleteComponent,
    TuiToggleModule,
    ReactiveFormsModule,
    CheckboxComponent,
    TuiGroupModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    TuiHintModule,
    TuiLetModule,
    CompareWithFieldDirective,
    TuiDataListDropdownManagerModule,
    TuiDataListModule,
    TuiDropdownModule,
    CargoPlaceDetailsComponent,
    NgForOf,
    TuiButtonModule,
    InputDateComponent,
  ],
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderComponent implements OnInit {
  update$ = new Subject<void>();
  openClientDialog$ = new Subject<'create' | 'edit'>();
  isActiveZone$ = new BehaviorSubject(false);
  openCreateBuyerDialog$ = new Subject<void>();
  openFarmDialog$ = new Subject<'create' | 'edit'>();
  addCustomLocation$ = new Subject<void>();
  hasBox$: Observable<boolean>;

  orderForm = this.fb.group<CargoOrderForm>({
    farmBrokerJson: this.forms.getFarmJsonForm(),
    prepaymentCurrencyLetter: this.fb.control(null),
    prepaymentSum: this.fb.control(null),
    brokerJson: this.forms.getSellerJsonForm(),
    expectedLoadingDate: this.fb.control(null),
    paymentDate: this.fb.control(null),
    prepayment: this.fb.control(null),
    id: this.fb.control<Nullable<number>>(null),
    uniqueKey: this.fb.control(null),
    cargoId: this.fb.control<Nullable<number>>(null),
    countryLetter: this.fb.control(''),
    airWaybillNumber: this.fb.control(''),
    precooling: this.fb.control<Nullable<boolean>>(null),
    weight: this.fb.control<Nullable<number>>(null),
    buyerCode: this.fb.control(''),
    farmJson: this.forms.getFarmJsonForm(),
    farmAddress: this.forms.getFarmAddressForm(),
    clientJson: this.forms.getClientJsonForm(),
    flowerType: this.fb.control(''),
    imported: this.fb.control<Nullable<boolean>>({
      value: null,
      disabled: true,
    }),
    cold: this.fb.control<Nullable<boolean>>(null),
    clientAddressWithCoordinatesJson:
      this.forms.getClientAddressWithCoordinatesJson(),
    attachmentId: this.fb.control<Nullable<number>>(null),
    invoiceCost: this.fb.control<Nullable<number>>(null),
    invoiceCurrencyLetter: this.fb.control<Nullable<string>>(null),
    invoiceDate: this.fb.control<Nullable<string>>(null),
    invoiceNumber: this.fb.control<Nullable<string>>(null),
    orderGroupId: this.fb.control<Nullable<number>>(null),
    preOrderId: this.fb.control<Nullable<number>>(null),
    rateJson: this.forms.getRateJsonForm(),
    precoolingWeight: this.fb.control<Nullable<number>>(null),
    cargoPlacesJson: this.fb.array<FormGroup<OrderCargoPlaceDetailsForm>>([]),
    divisionId: this.fb.control(null),
  });

  isImport$ = fromFormControlValue(this.orderForm.controls.imported);

  availableAddresses$ =
    this.addressesWithCoordinatesJsonControl.valueChanges.pipe(
      startWith([]),
      map(() => this.addressesWithCoordinatesJsonControl.getRawValue()),
    );

  preparedPackageTypes$ = this.catalogs.packageTypes$.pipe(
    map(packageTypes =>
      packageTypes.map(packageType => ({
        ...packageType,
        itemText: `${packageType.code} - ${packageType.name}`,
      })),
    ),
  );

  sellers$ = this.catalogs.sellers$;

  availableFarmAddresses$ = fromFormControlValue(
    this.orderForm.controls.farmJson.controls.addressesJson,
  );

  availableFlowerTypes$ = combineLatest([
    this.catalogs.flowerTypes$,
    fromFormControlValue(
      this.orderForm.controls.farmJson.controls.flowerTypesJson,
    ),
  ]).pipe(
    map(([flowerTypes, farmFlowerTypeIds]) => {
      if (
        !flowerTypes.length ||
        !farmFlowerTypeIds ||
        !farmFlowerTypeIds.length
      ) {
        return [];
      }
      return flowerTypes.filter(flowerType => {
        return farmFlowerTypeIds.includes(flowerType.id.toString());
      });
    }),
  );

  buyersOfSelectedFarm$ = combineLatest([
    fromFormControlValue(this.orderForm.controls.farmJson.controls.id),
    fromFormControlValue(this.orderForm.controls.clientJson.controls.id),
    this.catalogs.buyers$,
  ]).pipe(
    map(([farmId, clientId, buyers]) => {
      if (!farmId || !clientId) {
        return [];
      }
      return buyers.filter(
        buyer => buyer.farmId === farmId && buyer.clientId === clientId,
      );
    }),
  );

  countries = this.store.selectSignal(CatalogsState.getNsiCountries);
  cargoPlaceQuantityLimit = this.fb.control<Nullable<number>>({
    value: null,
    disabled: true,
  });

  trailerType$ = this.store
    .select(CargoLoadingState.getSelectedCargo)
    .pipe(map(cargo => cargo?.trailerType));

  preOrdersMap$ = this.store.select(CargoLoadingState.getPreOrdersMap);
  parentPreOrder$: Observable<Nullable<PreOrder>> = combineLatest([
    this.preOrdersMap$,
    fromFormControlValue(this.orderForm.controls.preOrderId),
  ]).pipe(
    map(([preOrdersMap, preOrderId]) => {
      return preOrdersMap.get(preOrderId) ?? null;
    }),
    shareReplay(),
  );

  constructor(
    protected readonly catalogs: CargoCatalogsService,
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly alert: AlertService,
    private readonly destroyRef: DestroyRef,
    private readonly commonState: CommonStateService,
    private readonly forms: FormsService,
    protected readonly cargoLoadingService: CargoLoadingService,
    private readonly dialogs: TuiDialogService,
  ) {}

  onFarmAddressChange(address: Nullable<string>) {
    this.setFarmAddressDetails(address);
  }

  onFarmChange() {
    const id =
      this.orderForm.controls.farmJson.controls.id.value?.toString() ?? '';
    this.setFarmDetails(id);
    this.clearFarmAddress();
    this.clearClientCodes();
    this.setDefaultCargoPlaceIfImport();
    this.fillFarmAddressIfSingle(this.farmJson.getRawValue());
  }

  onAddressChange() {
    const address =
      this.clientAddressWithCoordinatesJson.controls.address.value?.toString() ??
      '';

    if (!address) {
      this.clientAddressWithCoordinatesJson.reset();
    } else {
      const addresses = this.addressesWithCoordinatesJsonControl.getRawValue();
      const details = addresses.find(a => a.address === address);
      if (details) {
        this.clientAddressWithCoordinatesJson.patchValue(details);
      } else {
        this.clientAddressWithCoordinatesJson.reset();
      }
    }
    this.clientAddressWithCoordinatesJson.markAsDirty();
  }

  onClientChange() {
    const id =
      this.orderForm.controls.clientJson.controls.id.value?.toString() ?? '';
    this.setClientName(id);
    this.clearClientCodes();
    this.clearClientAddress();
    this.fillClientAddressIfSingle(this.clientJson.getRawValue());
  }

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
    this.isActiveZone$.next(focused);
  }

  onUpdate() {
    this.update$
      .pipe(
        delay(0),
        filter(() => !this.orderForm.pristine),
        switchMap(() => {
          const order = this.orderForm.getRawValue() as CargoOrder;
          return this.cargoLoadingService
            .updateOrder(order)
            .pipe(
              switchMap(() =>
                this.cargoLoadingService.recalculateAddresses(
                  order?.cargoId ?? 0,
                ),
              ),
            );
        }),
        tap(() => {
          this.alert.showSuccess();
          this.orderForm.markAsUntouched();
          this.orderForm.markAsPristine();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onCreateBuyerClick() {
    this.openCreateBuyerDialog$
      .pipe(
        switchMap(() => {
          const data: BuyerFormInputData = {
            buyer: {
              id: null,
              uniqueKey: null,
              clientId: this.clientJson.controls.id.value,
              farmId: this.farmJson.controls.id.value,
              code: this.orderForm.controls.buyerCode.value,
              name: null,
              publicAccess: null,
            },
          };
          return this.dialogs.open(
            new PolymorpheusComponent(BuyerFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data,
            },
          );
        }),
        tap(() => this.catalogs.fetchBuyers$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenClientDialogClick() {
    this.openClientDialog$
      .pipe(
        switchMap(type => {
          return this.dialogs.open(
            new PolymorpheusComponent(ClientFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data: type === 'edit' ? this.getClientFormInputData() : undefined,
            },
          );
        }),
        concatMap(() => {
          const nextClientUpdate$ = this.catalogs.clients$.pipe(
            skip(1),
            take(1),
          );

          this.catalogs.fetchClients$.next();

          return nextClientUpdate$;
        }),
        tap(() => this.syncAfterClientCatalogUpdate()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenFarmDialogClick() {
    this.openFarmDialog$
      .pipe(
        switchMap(type => {
          return this.dialogs.open(
            new PolymorpheusComponent(FarmFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data: type === 'edit' ? this.getFarmFormInputData() : undefined,
            },
          );
        }),
        concatMap(() => {
          const nextFarmUpdate$ = this.catalogs.farms$.pipe(skip(1), take(1));

          this.catalogs.fetchFarms$.next();

          return nextFarmUpdate$;
        }),
        tap(() => this.syncAfterFarmCatalogUpdate()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onAddCustomLocation() {
    return this.addCustomLocation$
      .pipe(
        switchMap(() =>
          this.dialogs
            .open<AddressWithCoordinates>(
              new PolymorpheusComponent(CustomLocationDialogComponent),
              {
                size: 'auto',
                dismissible: false,
                closeable: false,
              },
            )
            .pipe(
              tap(customAddressWithCoordinates => {
                const addressesWithCoordinatesJson =
                  this.orderForm.controls.clientJson.controls
                    .addressesWithCoordinatesJson;
                addressesWithCoordinatesJson.setValue([
                  ...addressesWithCoordinatesJson.getRawValue(),
                  customAddressWithCoordinates,
                ]);

                const clientAddressWithCoordinatesJson =
                  this.orderForm.controls.clientAddressWithCoordinatesJson;
                clientAddressWithCoordinatesJson.setValue(
                  customAddressWithCoordinates,
                );
                clientAddressWithCoordinatesJson.markAsTouched();
                clientAddressWithCoordinatesJson.markAsDirty();
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  syncAfterClientCatalogUpdate() {
    const id =
      this.orderForm.controls.clientJson.controls.id.value?.toString() ?? '';
    const currentAddress =
      this.orderForm.controls.clientAddressWithCoordinatesJson.controls.address.getRawValue();

    this.setClientName(id);

    const updatedClient = this.catalogs.clientMap.get(id.toString());

    const sameAddress = updatedClient?.addressesWithCoordinatesJson.find(
      a => a.address === currentAddress,
    );

    if (sameAddress) {
      this.orderForm.controls.clientAddressWithCoordinatesJson.setValue(
        sameAddress,
      );
    } else {
      this.clearClientAddress();
    }
  }

  syncAfterFarmCatalogUpdate() {
    const id =
      this.orderForm.controls.farmJson.controls.id.value?.toString() ?? '';
    const currentAddress =
      this.orderForm.controls.farmAddress.controls.address.getRawValue();

    this.setFarmDetails(id);

    const updatedFarm = this.catalogs.farmMap.get(id.toString());

    const sameAddress = updatedFarm?.addressesJson.find(
      a => a.address === currentAddress,
    );

    if (sameAddress) {
      this.orderForm.controls.farmAddress.setValue(sameAddress);
    } else {
      this.clearFarmAddress();
    }
  }

  clearFarmAddress() {
    this.farmAddress.reset();
  }

  get farmAddress() {
    return this.orderForm.controls.farmAddress;
  }

  fillClientAddressIfSingle(client: ClientJson) {
    if (client.addressesWithCoordinatesJson.length) {
      const [addressesWithCoordinatesJson] =
        client.addressesWithCoordinatesJson;
      this.orderForm.controls.clientAddressWithCoordinatesJson.setValue(
        addressesWithCoordinatesJson,
      );
    }
  }

  fillFarmAddressIfSingle(farm: Farm) {
    if (farm.addressesJson.length) {
      const [addressesJson] = farm.addressesJson;
      this.orderForm.controls.farmAddress.setValue(addressesJson);
    }
  }

  setFarmAddressDetails(addressJson: Nullable<string>) {
    if (!addressJson) {
      this.farmAddress.reset();
    } else {
      const farmAddress = this.farmJson.controls.addressesJson.value.find(
        a => a.address === addressJson,
      );
      farmAddress
        ? this.farmAddress.patchValue(farmAddress)
        : this.farmAddress.reset();
    }
    this.farmAddress.markAsDirty();
  }

  get clientAddressWithCoordinatesJson() {
    return this.orderForm.controls.clientAddressWithCoordinatesJson;
  }

  get addressesWithCoordinatesJsonControl() {
    return this.orderForm.controls.clientJson.controls
      .addressesWithCoordinatesJson;
  }

  clearClientAddress() {
    this.orderForm.controls.clientAddressWithCoordinatesJson.reset();
  }

  get farmJson() {
    return this.orderForm.controls.farmJson;
  }

  get clientJson() {
    return this.orderForm.controls.clientJson;
  }

  setFarmDetails(farmId: Nullable<string>) {
    if (!farmId) {
      this.orderForm.patchValue({
        flowerType: null,
        imported: null,
      });
      this.farmJson.reset();
    } else {
      const farm = this.catalogs.farmMap.get(farmId.toString());
      if (farm) {
        this.farmJson.patchValue(farm);
        this.orderForm.patchValue({
          flowerType: null,
          imported: farm.imported,
        });
      } else {
        this.farmJson.reset();
      }
    }
    this.farmJson.markAsDirty();
  }

  setDefaultCargoPlaceIfImport() {
    if (this.orderForm.controls.imported.getRawValue()) {
      const cargoPlace = this.catalogs.cargoPlaceMap.get(
        CargoPlaceType.PALLET_US,
      );
      this.orderForm.controls.cargoPlacesJson.controls.forEach(details => {
        if (cargoPlace) {
          details.controls.cargoPlaceJson.patchValue(cargoPlace);
        } else {
          details.controls.cargoPlaceJson.reset();
        }
      });
    }
  }

  setClientName(id: Nullable<string>) {
    if (!id) {
      this.clientJson.reset();
    } else {
      const client = this.catalogs.clientMap.get(id.toString());
      if (client) {
        this.clientJson.patchValue(client);
      } else {
        this.clientJson.reset();
      }
    }
    this.clientJson.markAsDirty();
  }

  clearClientCodes() {
    this.orderForm.controls.buyerCode.setValue(null);
  }

  @Input({ required: true }) set order(order: CargoOrder) {
    const { cargoPlacesJson, ...rest } = order;
    this.orderForm.patchValue(rest, { emitEvent: false });
    const cargoPlacesJsonControl = this.orderForm.controls.cargoPlacesJson;
    cargoPlacesJsonControl.clear({ emitEvent: false });
    cargoPlacesJson.forEach((details, index, array) => {
      const form = this.forms.getOrderCargoPlaceDetailsForm(details);
      form.controls.packageQuantityLimit.disable({ emitEvent: false });
      cargoPlacesJsonControl.push(form, {
        emitEvent: index === array.length - 1,
      });
    });
    this.orderForm.controls.brokerJson.disable({ emitEvent: false });
  }

  removeCargoPlaceDetails(index: number) {
    this.orderForm.controls.cargoPlacesJson.removeAt(index);
    this.orderForm.markAsTouched();
    this.orderForm.markAsDirty();
  }

  addCargoPlaceDetails() {
    const form = this.forms.getPreOrderCargoPlaceDetailsForm();
    form.controls.packageQuantityLimit.disable({ emitEvent: false });
    if (this.orderForm.controls.imported.value) {
      const cargoPlace = this.catalogs.cargoPlaceMap.get(
        CargoPlaceType.PALLET_US,
      );
      if (cargoPlace) {
        form.controls.cargoPlaceJson.patchValue(cargoPlace);
      }
    }
    this.orderForm.controls.cargoPlacesJson.push(form);
    this.orderForm.markAsTouched();
    this.orderForm.markAsDirty();
  }

  getClientFormInputData(): ClientFormInputData | undefined {
    const { id } = this.orderForm.controls.clientJson.value;
    const clientFromCatalog = id
      ? this.catalogs.clientMap.get(id.toString())
      : undefined;

    return {
      client: clientFromCatalog,
    };
  }

  getFarmFormInputData(): FarmFormInputData | undefined {
    const { id } = this.orderForm.controls.farmJson.value;
    const farmFromCatalog = id
      ? this.catalogs.farmMap.get(id.toString())
      : undefined;

    return {
      farm: farmFromCatalog,
    };
  }

  fillBrokerJson(sellerId: Nullable<string>) {
    const sellerJsonControl = this.orderForm.controls.brokerJson;
    const seller =
      this.catalogs.sellerMap.get(sellerId?.toString() ?? '') ?? null;
    if (seller) {
      sellerJsonControl.patchValue(seller);
    } else {
      sellerJsonControl.reset();
      sellerJsonControl.markAsDirty();
    }
  }

  ngOnInit() {
    this.onAddCustomLocation();
    this.onUpdate();
    this.onCreateBuyerClick();
    this.onOpenClientDialogClick();
    this.onOpenFarmDialogClick();
    fromFormGroupValue(this.orderForm)
      .pipe(
        withLatestFrom(this.cargoLoadingService.readonly$),
        tap(([, readonly]) => {
          if (readonly) {
            this.orderForm.disable();
          }
        }),
      )
      .subscribe();
  }
}
